import {
  type ActingNotePart,
  actingNote,
  isExpandedRepresentativeLine,
} from "./answering-attribution";
import type { AssessmentAttribution, MemberRepresentative } from "./types/answering-party";

/**
 * QL-654 — the representatives acting for a Group, from the server's
 * `AssessmentAttribution`, or null when the group is answered by its MEMBERS
 * (or by nobody yet). One representative gives `count` 1; 2+ representatives
 * (registered parties or flagged members: `members` with basis
 * `representatives`) are all named, `count` > 1 drives the plural wording.
 * Used by both the group answering notes and the risk step's K&E note so the
 * two never contradict each other.
 */
export function groupActingRepresentatives(
  attribution: AssessmentAttribution | null | undefined,
  language: string,
): { name: string | null; count: number; memberRepresentative?: MemberRepresentative } | null {
  if (!attribution) return null;
  if (attribution.kind === "representative") {
    // QL-660 — the group's flagged representative member acts through their own POA:
    // `name` is the POA, and the notes say "on behalf of {member}, who acts for the group".
    return {
      name: attribution.name,
      count: 1,
      ...(attribution.memberRepresentative && {
        memberRepresentative: attribution.memberRepresentative,
      }),
    };
  }
  if (attribution.kind === "members" && attribution.basis === "representatives") {
    // QL-653 — a flagged member whose own client has 2+ representatives is one line per
    // representative: they are named TOGETHER as one slot part; every other line by itself.
    const parts: ActingNotePart[] = [];
    const slots = new Map<string, MemberRepresentative[]>();
    for (const m of attribution.members) {
      if (!m.name) continue;
      if (m.memberRepresentative && isExpandedRepresentativeLine(m) && m.memberId) {
        const slot = slots.get(m.memberId);
        if (slot) {
          slot.push(m.memberRepresentative);
        } else {
          const representatives = [m.memberRepresentative];
          slots.set(m.memberId, representatives);
          parts.push({ kind: "slot", representatives });
        }
      } else {
        parts.push({ kind: "name", name: m.name });
      }
    }
    return actingNote(parts, language);
  }
  return null;
}
